import type { VerizonReplacementDevice } from "../api/prepareVerizonReplacement";
import type { ActivationFlowType, ClaimOrderSummaryForFrontend } from "../api/verifyCustomer";
import type { AccountMdn } from "../components/customerVerificationResult";
import type { GuideOption } from "../logic-builder";

export const ACTIVATION_ACTIONS = [
    "activateDevice",
    "authorizeVerizonInAcss",
    "generatePasscode",
    "generatePasscodeForVerification",
    "prepareVerizonReplacement",
    "routeVerizonReplacementDevice",
    "selectClaim",
    "selectVerizonReplacement",
    "setActivationTypeToNew",
    "setCustomerState",
    "submitTypedVerizonDevice",
    "typeVerizonImei",
    "verifyPasscode",
    "verifyPasscodeForVerification",
    "verifyPin",
    "verifyVerizonPin",
] as const;

export type ActivationAction = (typeof ACTIVATION_ACTIONS)[number];

export const isActivationAction = (action?: string): action is ActivationAction =>
    !!action && (ACTIVATION_ACTIONS as readonly string[]).includes(action);

export interface ActivationActionInput {
    /**
     * Whether the step renders a dropdown at all. `generatePasscode` may be fired from a plain button,
     * in which case there is no selection to demand — see the handler for why the two differ.
     */
    hasDropdown: boolean;
    inputValue: string;
    option: GuideOption;
    selectedOption?: GuideOption;
}

/**
 * `recoveryNeeded`: the action refused to call the backend because guide state it depends on (the
 * customer MDN, or the verification token) is missing. The card stays on the current step and
 * `UseDeviceActivation.recoveryNotice` tells the expert how to recover.
 */
export type ActivationActionResult =
    | { status: "next"; nextStep?: string }
    | { status: "needsDropdownSelection" }
    | { status: "recoveryNeeded" };

/**
 * Customer answers and verification results collected across the Device Activation guide's steps.
 * Lives in the guide store (not component state) so it survives the card unmounting mid-guide.
 * Never holds the PIN or a passcode: those are sent straight to the backend and dropped.
 */
export interface DeviceActivationCustomerState {
    accountMdns: AccountMdn[];
    activationFlowType?: ActivationFlowType;
    claimSummaries: ClaimOrderSummaryForFrontend[];
    iccid: string;
    imei: string;
    phoneNumber: string;
    selectedClaimId: string;
    transactionId: string;
    /**
     * Verizon only: the replacement device to activate, as shown to the expert. `prepareVerizonReplacement` sets
     * the newest it found, and `selectVerizonReplacement` the one the expert picked.
     */
    verizonReplacement?: VerizonReplacementDevice;
}
